import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import { canUserEdit } from '../../../utils/rightUtils';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IAddress,
  IClientOptions,
  ICompany,
  IContact,
  useInvoiceClientOptionsLazyQuery
} from '../../../generated/graphql';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { HomeWork, LocationOn, Person } from '@mui/icons-material';

interface IProps {
  cventContact?: IContact | null;
}

const ClientStep: React.FunctionComponent<IProps> = (cventContact) => {
  const hasCventContact =
    cventContact.cventContact !== null && cventContact.cventContact !== undefined;
  const { dispatch, state } = useContext(AppContext);
  const [clientOptions, setClientOptions] = useState<IClientOptions | null>(null);
  const [companyOptions, setCompanyOptions] = useState<ICompany[]>([]);
  const [contactOptions, setContactOptions] = useState<IContact[]>([]);
  const [addressOptions, setAddressOptions] = useState<IAddress[]>([]);
  const [getInvoiceOptions, { data }] = useInvoiceClientOptionsLazyQuery({
    variables: {
      membership_id: parseInt(state.selectedMembership?.id as string)
    },
    fetchPolicy: 'no-cache'
  });
  const [company, setCompany] = useState<ICompany | null>(
    state.selectedInvoice?.company ??
      ((cventContact.cventContact as IContact)?.company as ICompany) ??
      null
  );
  const [contact, setContact] = useState<IContact | null>(
    state.selectedInvoice?.contact ?? (cventContact.cventContact as IContact) ?? null
  );
  const [address, setAddress] = useState<IAddress | null>(
    state.selectedInvoice?.address ??
      ((cventContact.cventContact as IContact)?.address as IAddress) ??
      null
  );
  const canEdit = canUserEdit(state.user, Types.Invoice);

  const editable =
    canEdit &&
    (state.selectedInvoice?.status === InvoiceStatus.New ||
      state.selectedInvoice?.status === InvoiceStatus.Pending) &&
    !hasCventContact;

  useEffect(() => {
    if (editable && !clientOptions && !hasCventContact) {
      getInvoiceOptions();
    }
  }, []);

  useEffect(() => {
    if (state.selectedInvoice?.status === InvoiceStatus.Confirmed) {
      getInvoiceOptions();
    }
  }, []);

  useEffect(() => {
    if (hasCventContact && (contact === null || contact?.id !== cventContact.cventContact?.id)) {
      setContact(cventContact.cventContact as IContact);
      setCompany((cventContact.cventContact as IContact)?.company as ICompany);
      setAddress((cventContact.cventContact as IContact)?.address as IAddress);
      dispatch({
        type: Actions.SetInvoiceClientDetails,
        payload: {
          company: (cventContact.cventContact as IContact)?.company as ICompany,
          address: (cventContact.cventContact as IContact)?.address as IAddress,
          contact: cventContact.cventContact as IContact
        }
      });
    }
  }, [cventContact]);

  useEffect(() => {
    const errors = { ...state.selectedInvoice?.errors };
    if (company) {
      delete errors.company;
    }
    if (contact) {
      delete errors.contact;
    }
    if (address) {
      delete errors.address;
    }
    dispatch({
      type: Actions.SetInvoiceErrors,
      payload: {
        ...errors
      }
    });
  }, [company, contact, address]);

  useEffect(() => {
    if (data && data.result && !hasCventContact) {
      const companyOpts = data.result.companies;
      const contactOpts = data.result.contacts;
      const addressOpts = [
        ...new Set(data.result.contacts.map((c) => c?.address).filter((a) => a != null))
      ];
      const errors = { ...state.selectedInvoice?.errors };
      if (company && companyOpts.findIndex((c) => c?.name === company.name) === -1) {
        errors.company = 'Company has been deleted. Please select another one';
      }
      if (contact && contactOpts.findIndex((c) => c?.fullName === contact.fullName) === -1) {
        errors.contact = 'Contact has been deleted. Please select another one';
      }
      if (address && addressOpts.findIndex((a) => a?.name === address.name) === -1) {
        errors.address = 'Address  has been deleted. Please select another one';
      }
      dispatch({
        type: Actions.SetInvoiceErrors,
        payload: {
          ...errors
        }
      });
      setClientOptions(data.result as IClientOptions);
      setCompanyOptions(companyOpts as ICompany[]);
      setContactOptions(contactOpts as IContact[]);
      setAddressOptions(addressOpts as IAddress[]);
    }
  }, [data?.result]);

  const handleCompanyChanged = (_: unknown, newValue: ICompany | null) => {
    setCompany(newValue);
    let contactValue = null;
    let addressValue = null;

    let contactOpts: IContact[] = [];
    let addressOpts: IAddress[] = [];
    if (newValue) {
      const possibleContacts = clientOptions?.contacts.filter(
        (c) => c?.company.id === newValue.id
      ) as IContact[];
      contactOpts = possibleContacts;
      addressOpts = possibleContacts.map((c) => c.address) as IAddress[];
      addressOpts = [...new Set(addressOpts.filter((a) => a != null) as never[])];
      if (contactOpts?.length === 1) {
        setContact(contactOpts[0]);
        contactValue = contactOpts[0];
      } else {
        setContact(null);
        contactValue = null;
      }
      if (addressOpts.length === 1) {
        setAddress(addressOpts[0]);
        addressValue = addressOpts[0];
      } else {
        setAddress(null);
        addressValue = null;
      }
    } else {
      setContact(null);
      contactValue = null;

      setAddress(null);
      addressValue = null;

      contactOpts = clientOptions?.contacts as IContact[];
      addressOpts = [
        ...new Set(
          clientOptions?.contacts.map((c) => c?.address).filter((a) => a != null) as IAddress[]
        )
      ];
    }
    dispatch({
      type: Actions.SetInvoiceClientDetails,
      payload: {
        company: newValue,
        address: addressValue,
        contact: contactValue
      }
    });
    dispatch({
      type: Actions.ResetInvoiceErrors
    });
    setContactOptions(contactOpts as never[]);
    setAddressOptions(addressOpts as never[]);
  };

  const handleContactChanged = (_: unknown, newValue: IContact | null) => {
    setContact(newValue);
    let companyValue = company;
    let addressValue = address;
    let clientOption = null as IContact | null | undefined;
    if (newValue) {
      if (newValue.address) {
        addressValue = newValue.address;
        setAddress(newValue.address);
      }
      clientOption = clientOptions?.contacts.find((c) => c?.id === newValue.id);
      if (!company) {
        setCompany(clientOption?.company as ICompany);
        companyValue = clientOption?.company as ICompany;
        const addresses = [
          ...new Set(
            clientOptions?.contacts
              .filter((c) => c?.company.id === clientOption?.company.id)
              .map((c) => c?.address)
              .filter((a) => a != null) as IAddress[]
          )
        ];
        if (addresses.length === 1 && !newValue.address) {
          addressValue = addresses[0];
          setAddress(addresses[0]);
        }
        setAddressOptions(addresses);
      }
    }
    dispatch({
      type: Actions.SetInvoiceClientDetails,
      payload: {
        company: companyValue,
        contact: newValue,
        address: addressValue
      }
    });
  };

  const handleAddressChanged = (_: unknown, newValue: IAddress | null) => {
    setAddress(newValue);
    dispatch({
      type: Actions.SetInvoiceClientDetails,
      payload: {
        address: newValue
      }
    });
  };

  return (
    <Box
      sx={{
        pt: '142px',
        px: '31.25%',
        display: 'grid',
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        gap: '60px'
      }}
    >
      <AutocompleteComponent
        id="invoice-company"
        data-testid="invoice-company"
        label="Company"
        options={companyOptions}
        disabled={!editable || state.selectedInvoice?.status === InvoiceStatus.ConfirmedCredit}
        values={company}
        onChange={handleCompanyChanged}
        getOptionLabel={(option: ICompany) => option?.name as string}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        errorMsg={state.selectedInvoice?.errors?.company}
        startAdornment={
          <HomeWork
            sx={{
              color: (theme) =>
                state.selectedInvoice?.errors?.company
                  ? theme.palette.error.main
                  : theme.palette.primary.main
            }}
          />
        }
      />
      <AutocompleteComponent
        id="invoice-contact-name"
        data-testid="invoice-contact-name"
        label="Contact name"
        options={contactOptions}
        disabled={
          (!editable && state.selectedInvoice?.status !== InvoiceStatus.Confirmed) ||
          state.selectedInvoice?.status === InvoiceStatus.ConfirmedCredit
        }
        values={contact}
        onChange={handleContactChanged}
        getOptionLabel={(option: IContact) => option.fullName as string}
        isOptionEqualToValue={(option, value) => option.fullName === value.fullName}
        errorMsg={state.selectedInvoice?.errors?.contact}
        startAdornment={
          <Person
            sx={{
              color: (theme) =>
                state.selectedInvoice?.errors?.contact
                  ? theme.palette.error.main
                  : theme.palette.primary.main
            }}
          />
        }
      />
      <AutocompleteComponent
        id="invoice-contact-address"
        data-testid="invoice-contact-address"
        label="Contact address"
        options={addressOptions}
        disabled={!editable || state.selectedInvoice?.status === InvoiceStatus.ConfirmedCredit}
        onChange={handleAddressChanged}
        values={address}
        getOptionLabel={(option: IAddress) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        errorMsg={state.selectedInvoice?.errors?.address}
        startAdornment={
          <LocationOn
            sx={{
              color: (theme) =>
                state.selectedInvoice?.errors?.address
                  ? theme.palette.error.main
                  : theme.palette.primary.main
            }}
          />
        }
      />
    </Box>
  );
};

export default ClientStep;
